import { useCallback, useMemo, useState } from "react";

import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import Box from "ds/components/Box";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerForm from "ds/components/DrawerNew/Form";
import Callout from "ds/components/Callout";
import AssignRoleForm from "components/AssignRoleForm";
import { AssignRoleFormAddFields } from "components/AssignRoleForm/types";
import { CREATE_FORM_ID } from "components/AssignRoleForm/constants";
import useFlowsProject from "shared/Flows/Projects/useFlowsProject";
import useFlowsProjectRoleBindingBatchCreate from "shared/Flows/Projects/useFlowsProjectRoleBindingBatchCreate";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import { pluralize } from "shared/Pluralize";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageFlows } from "hooks/useAnalytics/pages/flows";
import { Action } from "types/generated";
import { isRootSpace } from "utils/space";

import { showFlowsProjectRoleUnassignConfirmation } from "../UnassignConfirmation";
import { FLOWS_PROJECT_ROLES_REFETCH_QUERIES } from "../constants";

type FlowsProjectDetailsRolesManageRolesDrawerProps = {
  projectId: string;
};

const FlowsProjectDetailsRolesManageRolesDrawer = createDrawer(
  ({ projectId }: FlowsProjectDetailsRolesManageRolesDrawerProps) => {
    const { reportSuccess } = useTypedContext(FlashContext);
    const trackSegmentEvent = useAnalytics({
      page: AnalyticsPageFlows.FlowsProjectRoles,
    });

    const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({
      [CREATE_FORM_ID]: false,
    });

    const setIsDirtyHandler = useCallback(
      (formId: string, value: boolean) => setDirtyFields((prev) => ({ ...prev, [formId]: value })),
      []
    );

    const { roleBindings, roles, loading } = useFlowsProject({ projectId });

    // Only a root-space binding whose role grants SpaceAdmin gives account-wide admin access.
    // A read/write role at root grants that role's access account-wide, but is not admin.
    const hasAdminRootSpaceBinding = useMemo(
      () =>
        roleBindings.some(
          (binding) =>
            isRootSpace(binding.space.id) && binding.role.actions.includes(Action.SpaceAdmin)
        ),
      [roleBindings]
    );

    const assignedRoles = useMemo(
      () =>
        roleBindings.map((binding) => ({
          id: binding.id,
          roleID: binding.role.id,
          spaceID: binding.space.id,
          spaceName: binding.space.name,
        })),
      [roleBindings]
    );

    const [batchCreateFlowsProjectRoleBinding, { loading: createBatchRoleBindingLoading }] =
      useFlowsProjectRoleBindingBatchCreate({
        refetchQueries: FLOWS_PROJECT_ROLES_REFETCH_QUERIES,
      });

    const onCreate = useCallback(
      (formData: AssignRoleFormAddFields) => {
        const bindings = formData.roleIds.map((roleId) => ({
          roleID: roleId,
          spaceID: formData.space,
          flowsProjectID: projectId,
        }));

        return batchCreateFlowsProjectRoleBinding(
          {
            input: {
              bindings,
            },
          },
          (data) => {
            if (data?.flowsProjectRoleBindingBatchCreate?.length) {
              const count = data.flowsProjectRoleBindingBatchCreate.length;
              reportSuccess({
                message: `${pluralize("Role", count)} successfully assigned`,
              });
              trackSegmentEvent("Flows project role binding added", { count });
            }
          }
        );
      },
      [batchCreateFlowsProjectRoleBinding, projectId, reportSuccess, trackSegmentEvent]
    );

    const isDirty = useMemo(
      () => !!Object.values(dirtyFields).filter(Boolean).length,
      [dirtyFields]
    );

    const onDelete = useCallback(
      (roleAssignmentId: string, roleName: string) =>
        showFlowsProjectRoleUnassignConfirmation({
          id: roleAssignmentId,
          name: roleName,
          refetchQueries: FLOWS_PROJECT_ROLES_REFETCH_QUERIES,
          source: "drawer",
        }),
      []
    );

    return (
      <DrawerForm isDirty={isDirty}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Assign roles" />
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody>
          <Box direction="column" gap="large">
            {hasAdminRootSpaceBinding && (
              <Callout variant="warning">
                This project has an admin role assigned in the <strong>root</strong> space, which
                grants it administrative access to your entire account. Only keep this if it is
                intended.
              </Callout>
            )}
            <AssignRoleForm
              onCreate={onCreate}
              setDirtyFields={setIsDirtyHandler}
              availableRoles={roles}
              assignedRoles={assignedRoles}
              loading={loading}
              createLoading={createBatchRoleBindingLoading}
              onDelete={onDelete}
            />
          </Box>
        </DrawerBody>
      </DrawerForm>
    );
  }
);

export const showFlowsProjectDetailsRolesManageRolesDrawer = createDrawerTrigger(
  FlowsProjectDetailsRolesManageRolesDrawer
);
